import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Card, Col, Container, Row, Table, Form, Spinner, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import { useTable, useSortBy } from "react-table";


import NotyfContext from "../contexts/NotyfContext";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import authenticatedFetch from "../utils/fetch"
import DocumentList from "../utils/DocumentList"
import EditableDetail from "../utils/EditableDetail"
import UsStateSelect from "../utils/UsStateSelect"
import EditableAddress from "../utils/EditableAddress";
import { CopyText, CopyIds } from "../components/header/HeaderParts";
import ReadOnlyAddress from "../utils/ReadOnlyAddress";
import DetailCardHeader from "../utils/DetailCardHeader";
import { DetailSection, DetailField } from "../utils/DetailParts";
import HubParticipationStatusTable from "../utils/HubParticipationStatusTable";
import OpenTasks from "../utils/OpenTasks";
import { cardOutlineStyle } from "../utils/TaskCardParts";
import ParticipationStatusIcon from "../utils/ParticipationStatusIcon";

import { ProviderAvatar } from "../components/provider/ProviderHeader";
import PracticeHeader from "../components/practice/PracticeHeader";
import PracticeEditPanel from "../components/practice/PracticeEditPanel";
import { toPracticePocRequests } from "../utils/practicePocTypes";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faFilePdf,
  faFileCsv,
  faIdBadge,
  faUserEdit,
} from "@fortawesome/free-solid-svg-icons";


const EmailList = ({ value }) => {
  const emails = (value || "").split(",").map((e) => e.trim()).filter(Boolean);
  return emails.length ? emails.map((e) => <div key={e} style={{ wordBreak: "break-all" }}>{e}</div>) : null;
};

const PracticeOnboardingUrl = ({practiceId}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);


  useEffect(() => {
    authenticatedFetch({
      path: "practice/onboarding/url/" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [practiceId])


  if (!error && isLoaded && data && data.hasActiveOnboardingUrl)
  {
    return (
      <OverlayTrigger
        trigger={["hover", "focus"]}
        placement="bottom"
        overlay={<Tooltip>This practice is actively completing their onboarding. They have the ability to edit data from various sections of their profile.</Tooltip>}
      >
        <span className="text-warning ms-2" style={{fontSize: "0.8em"}}>
          <FontAwesomeIcon icon={faUserEdit} />
        </span>
      </OverlayTrigger>
    );
  }

  return <></>;
}

const Provider = ({ photoUrl, practiceId, id, lastName, firstName, title, npi, isFacilityList=false, carrierCount=1 }) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  const { isH3Admin, authorities, tasksRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    authenticatedFetch({
      path: "provider/participationstatus/" + (isFacilityList ? "facility/" : "") + id + "/" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [id, practiceId, isFacilityList, tasksRefresh])

  var carrierList;

  if (error)
  {
    carrierList = <td colSpan={carrierCount}>Something went wrong... please reload the page...</td>;
  }
  else if (!isLoaded)
  {
    carrierList = <td colSpan={carrierCount}>Loading...</td>;
  }
  else
  {
    var res = isFacilityList ? data.hubs : data.carriers;
    
    carrierList = res && res.length > 0
      && res.map((carrier, i) => {
      return (
        <td key={carrier.id}>
          <ParticipationStatusIcon notApplicable={carrier.isNotApplicable} inProgress={carrier.isInProgress} isPending={carrier.isPending} pendingTimestamp={carrier.pendingTimestamp} isActive={carrier.isActive} isStopped={isFacilityList && carrier.privilegesType && (carrier.privilegesType === 'VOLUNTARILY_RESIGNED' || carrier.privilegesType === 'EXPIRED')} />
        </td>
      )
    }, this);
  }

  return (
    <>
        <td>
          <ProviderAvatar data={{photoUrl: photoUrl, firstName: firstName, lastName: lastName}} size={32} />
        </td>
        <td>
          {(isH3Admin || authorities.includes("PROVIDER_BASIC_EDIT") || authorities.includes("PROVIDER_BASIC_VIEW")) ?
            <a href={"/provider/" + id}>
            {lastName + ","}&nbsp;{firstName}
            </a>
          : <>{lastName + ","}&nbsp;{firstName}</>
          }
        </td>
        <td>{title}</td>
        {carrierList}
    </>
  );
}

function stripCredential(credential) {
  if (credential == null)
  {
    return '';
  }

  return credential.toLowerCase().replace(/[^a-z]/g, '').trim();
}

const getCredentialRankValueString = (credential) => {
  if (credential != null && credential !== '')
  {
    credential = stripCredential(credential);
    if ((credential.indexOf("md") > -1 || credential.indexOf("do") > -1 || credential.indexOf("dpm") > -1) && credential.indexOf("mdt") <= 0)
    {
      return "5"
    }
    if (credential.indexOf("pa") > -1 || credential.indexOf("ma") > -1 || credential.indexOf("np") > -1)
    {
      return "4";
    }
    if (credential.indexOf("pt") > -1)
    {
      return "3";
    }
    
    return "2";
  }

  return "1";
};

const nullSafeLocaleCompare = (a, b) => {
  return (a || '').localeCompare(b || '');
};

const providerNameSort = (rowA, rowB) => {
  if (rowA.original.lastName === rowB.original.lastName) {

    if (rowA.original.firstName === rowB.original.firstName) {
      return nullSafeLocaleCompare(rowA.original.middleName, rowB.original.middleName);
    }

    return nullSafeLocaleCompare(rowA.original.firstName, rowB.original.firstName);
  }

  return nullSafeLocaleCompare(rowA.original.lastName, rowB.original.lastName);
};

const FIXED_COLUMN_KEYS = ["photoUrl", "provider", "credentials"];
const isCarrierColumn = (column) => !FIXED_COLUMN_KEYS.includes(column.key);

// Must match the pinned column widths (avatar + provider + creds) in _tables.scss
const ENROLLMENT_PINNED_WIDTH = 40 + 125 + 58;
const ENROLLMENT_CARRIER_MIN_WIDTH = 34;
// The PDF export gets unreadable past this many carrier columns (CSV is always offered)
const MAX_CARRIERS_FOR_PDF_EXPORT = 50;

const ProviderSortableList = ({practiceId, isFacilityList=false}) => {
  const [error, setError] = useState(null);
  const [isHeaderDataLoaded, setIsHeaderDataLoaded] = useState(false);
  const [isProviderDataLoaded, setIsProviderDataLoaded] = useState(false);
  const [headerData, setHeaderData] = useState([]);
  const [providerData, setProviderData] = useState([]);

  const [isExporting, setIsExporting] = useState(false);

  const notyf = useContext(NotyfContext);

  useEffect(() => {
    authenticatedFetch({
      path: "provider/practice/" + practiceId,
      successHandler: function(result) {
        setIsProviderDataLoaded(true);
        setProviderData(result);
      },
      errorHandler: function(error) {
        setIsProviderDataLoaded(true);
        setError(error);
      }
    });

    authenticatedFetch({
      path: "practice/participationstatus/" + (isFacilityList ? "facility/" : "") + practiceId,
      successHandler: function(result) {
        setIsHeaderDataLoaded(true);
        setHeaderData(result);
      },
      errorHandler: function(error) {
        setIsHeaderDataLoaded(true);
        setError(error);
      }
    });
  }, [practiceId, isFacilityList])

  const columns = React.useMemo(
    () => {
      var headers = [
        {
          key: "id",
          accessor: "id",
        },
        {
          key: "photoUrl",
          accessor: "photoUrl",
        },
        {
          key: "lastName",
          accessor: "lastName",
        },
        {
          key: "firstName",
          accessor: "firstName",
        },
        {
          key: "middleName",
          accessor: "middleName",
        },
        {
          Header: "Provider",
          key: "provider",
          id: "provider",
          accessor: () => {return null},  // Have to have something here or the sort props don't work
          sortType: (rowA, rowB, columnId, desc) => {
            return providerNameSort(rowA, rowB);
          },
        },
        {
          Header: <OverlayTrigger placement="top" overlay={<Tooltip>Credentials</Tooltip>}><span><FontAwesomeIcon icon={faIdBadge} size="lg"/></span></OverlayTrigger>,
          accessor: "credentials",
          key: "credentials",
          sortType: (rowA, rowB, columnId, desc) => {
            var rankA = getCredentialRankValueString(rowA.values[columnId]);
            var rankB = getCredentialRankValueString(rowB.values[columnId]);

            if (rankA === rankB)
            {
              if (stripCredential(rowA.values[columnId]) === stripCredential(rowB.values[columnId]))
              {
                // We want credentials "DESC" (so MD first), but then name *ASC*
                return -1 * providerNameSort(rowA, rowB);
              }

              return nullSafeLocaleCompare(rowA.values[columnId], rowB.values[columnId]);
            }
            
            return rankA.localeCompare(rankB);
          },
        }
      ];

      var headerDataToUse = [];
      if (isFacilityList)
      {
        headerDataToUse = headerData.hubs;
      }
      else
      {
        headerDataToUse = headerData.carriers;
      }

      if (isHeaderDataLoaded && headerDataToUse && headerDataToUse.length > 0)
      {
        headers = headers.concat(headerDataToUse.map((carrier, i) => {
          return (
            {
              Header: carrier.name,
              id: carrier.id,
            }
          )
        }, this));
      }

      return headers;
    },
  [headerData, isHeaderDataLoaded, isFacilityList]
  );


  const enrollmentCarrierList = isFacilityList ? headerData.hubs : headerData.carriers;
  const enrollmentCarrierCount = isHeaderDataLoaded && enrollmentCarrierList ? enrollmentCarrierList.length : 0;

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state: {sortBy} } =
  useTable(
    {
      columns,
      data: providerData,
      autoResetSortBy: false,
      initialState: {
        hiddenColumns: ['id', 'lastName', 'firstName', 'middleName'],
        sortBy: [
          {
              id: 'provider',
              desc: false
          },
        ]
      }
    },
    useSortBy,
  );

  const handleExportClicked = (e, reportFormat) => {
    e.preventDefault();

    var sort = '';
    if (sortBy.length > 0)
    {
      sort = sortBy[0].id + (sortBy[0].desc ? 'Desc' : 'Asc');
    }

    setIsExporting(true);

    authenticatedFetch({
      method: "GET",
      path:  "report/enrollmentmatrix/" + (isFacilityList ? "facility/" : "carrier/")  + "practice/" + practiceId + "?sort=" + sort + "&reportFormat=" + reportFormat,
      successHandler: function(result) {
        window.open(result.url);
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  // Message rows span every column, pinned and carrier
  const matrixColumnCount = headerGroups.length > 0 ? headerGroups[headerGroups.length - 1].headers.length : 1;

  return (
    <Card style={cardOutlineStyle}>
      <Card.Header style={{marginBottom: '-15px'}}>
          <Card.Title>
              {isFacilityList ? "Location" : "Enrollment"} Status
              {!error && isProviderDataLoaded &&
                <Badge bg="light" text="secondary" pill className="ms-2" title="Total" style={{fontSize: "0.6em", verticalAlign: "middle", border: "1px solid #dee2e6"}}>{rows.length}</Badge>
              }
              {isProviderDataLoaded && providerData && providerData.length > 0 && isHeaderDataLoaded && enrollmentCarrierCount > 0 &&
              <>
                { isExporting ? 
                  <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '12px', marginTop: '4px'}}/>
                  :
                  <Form.Label style={{display: "inline", fontSize: "0.8em", fontWeight: "normal", marginTop: "7px", marginRight: "3px"}} className="float-end">
                    Export:&nbsp;&nbsp;
                    {enrollmentCarrierCount < MAX_CARRIERS_FOR_PDF_EXPORT &&
                    <>
                    <FontAwesomeIcon onClick={(e) => {handleExportClicked(e, "PDF")}} icon={faFilePdf} size="lg" style={{color: '#2B7A78', cursor: "pointer"}}/>
                    &nbsp;/&nbsp;
                    </>
                    }
                    <FontAwesomeIcon onClick={(e) => {handleExportClicked(e, "CSV")}} icon={faFileCsv} size="lg" style={{color: '#2B7A78', cursor: "pointer"}}/>
                    &nbsp;
                  </Form.Label>
                }
              </>
              }
          </Card.Title>
        </Card.Header>
      <Card.Body style={{overflow: 'scroll', maxHeight: '700px', paddingTop: 0}}>
        <Table striped hover className="enrollment-matrix" style={{minWidth: ENROLLMENT_PINNED_WIDTH + enrollmentCarrierCount * ENROLLMENT_CARRIER_MIN_WIDTH}} {...getTableProps()}>
          <thead style={{position: 'sticky', top: 0, background: '#FFFFFF'}}>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const { key, style, ...restHeaderProps } = (column.key !== "provider" && column.key !== "credentials") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
                    return (
                      <th {...restHeaderProps} key={key} className={isCarrierColumn(column) ? "em-carrier-th" : undefined} style={{...style, whiteSpace: (column.key === "provider" || column.key === "credentials") ? "nowrap" : ""}}>
                        {isCarrierColumn(column) ? <span className="em-carrier-label" title={column.Header}>{column.render("Header")}</span> : column.render("Header")}
                        {(column.key === "provider" || column.key === "credentials") ? (
                          <>
                          {column.isSorted ? (
                              column.isSortedDesc ? (
                                <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                              ) : (
                                <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                              )
                            ) : (
                              <FontAwesomeIcon icon={faSort} className="ms-2" />
                          )}
                          </>
                        ) : <></>
                        }
                      </th>
                    )
                  }
                  )}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {
              error ? (<tr><td colSpan={matrixColumnCount}>Something went wrong; please reload the page...</td></tr>) : (
                !isProviderDataLoaded ? (<tr><td colSpan={matrixColumnCount}>Loading...</td></tr>) : (
                rows.map((row) => {
                  prepareRow(row);
                  const { key, ...restRowProps } = row.getRowProps();
                  return (
                    <tr {...restRowProps} key={key}>
                      <Provider key={row.values.id} id={row.values.id} practiceId={practiceId} photoUrl={row.values.photoUrl} lastName={row.values.lastName} firstName={row.values.firstName} title={row.values.credentials} npi={row.values.npi} isFacilityList={isFacilityList} carrierCount={Math.max(1, enrollmentCarrierCount)}/>
                    </tr>
                  );
                })))
              }
            </tbody>
          </Table>
      </Card.Body>
    </Card>
  )
};


const EditablMailingAddress = ({address1, address2, city, state, zip, county, onValueChanged}) => {
  return (
    <>
    <EditableDetail placeholder="Maililng address 1" originalValue={address1} name="mailingAddress1" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Maililng address 2" originalValue={address2} name="mailingAddress2" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Maililng city" originalValue={city} name="mailingCity" onValueChanged={onValueChanged}/>
    <UsStateSelect placeholder="Maililng state" originalValue={state} name="mailingState" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Maililng zip" originalValue={zip} name="mailingZip" mask="99999-9999" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Mailing county" originalValue={county} name="mailingCounty" onValueChanged={onValueChanged}/>
    </>
  );
}

const EditablRemitAddress = ({address1, address2, city, state, zip, county, onValueChanged}) => {
  return (
    <>
    <EditableDetail placeholder="Remit address 1" originalValue={address1} name="remitAddress1" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Remit address 2" originalValue={address2} name="remitAddress2" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Remit city" originalValue={city} name="remitCity" onValueChanged={onValueChanged}/>
    <UsStateSelect placeholder="Remit state" originalValue={state} name="remitState" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Remit zip" originalValue={zip} name="remitZip" mask="99999-9999" onValueChanged={onValueChanged}/>
    <EditableDetail placeholder="Remit county" originalValue={county} name="remitCounty" onValueChanged={onValueChanged}/>
    </>
  );
}

const Practice = () => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false); 
  const [editingSettings, setEditingSettings] = useState(false);
  const [localData, setLocalData] = useState([])
  const [isExpanded, setIsExpanded] = useState(true);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [forceSpecialtyRefresh, setForceSpecialtyRefresh] = useState(0);

  let { id } = useParams();

  const notyf = useContext(NotyfContext);

  const { isH3Admin, authorities, navbarRefresh, setNavbarRefresh } = useContext(UserSettingsContext);

  useEffect(() => {
    setEditing(false);

    authenticatedFetch({
      path: "practice/" + id,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
        // Otherwise seem to point to the same obj, and updating one updates the other?
        var copy={};
        Object.assign(copy, result);
        setLocalData(copy);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }, [id])

  const handleExpandClicked = (e) => {
    e.preventDefault();
    setIsExpanded(true);
  }

  const handleCollapseClicked = (e) => {
    e.preventDefault();
    setIsExpanded(false);
  }

  const handleSettingsExpandClicked = (e) => {
    e.preventDefault();
    setIsSettingsExpanded(true);
  }

  const handleSettingsCollapseClicked = (e) => {
    e.preventDefault();
    setIsSettingsExpanded(false);
  }

  const handleEditClicked = () => {
    // Treat it like a cancel of the other one if it was open, for simplicity
    setEditingDetails(false);
    setEditingSettings(false);
    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);

    setEditing(true);
  }

  const handleEditDetailsClicked = () => {
    setEditing(false);
    setEditingSettings(false);
    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);

    setEditingDetails(true);
  }

  const handleEditSettingsClicked = () => {
    setEditing(false);
    setEditingDetails(false);
    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);

    setEditingSettings(true);
  }


  const handleSaveClicked = (editDetails, editSettings) => {
    setEditing(false);
    setEditingDetails(false);
    setEditingSettings(false);

    authenticatedFetch({
      path: "practice/" + id,
      method: "POST",
      requestData: {...localData, practicePocs: toPracticePocRequests(localData.practicePocs)},
      successHandler: function(result) {
        setData(result);
        var copy={};
        Object.assign(copy, result);
        setLocalData(copy);
        notyf.open({type: "success", message: "Practice information updated!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        setNavbarRefresh(navbarRefresh + 1);
      },
      errorHandler: function(error) {
        if (editDetails)
        {
          setEditingDetails(false);
        }
        else if (editSettings)
        {
          setEditingSettings(false);
        }
        else
        {
          setEditing(true);
        }
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const handleCancelClicked = () => {
    setEditing(false);
    setEditingDetails(false);
    setEditingSettings(false);

    var copy={};
    Object.assign(copy, data);
    setLocalData(copy);
  }

  const onValueChanged = (name, newValue) => {
    if (newValue != null && name != null && (name.toLowerCase().indexOf("zip") > -1 || name.indexOf("phone") > -1 || name.indexOf("fax") > -1 || name.indexOf("npi") > -1 || name.indexOf("tin") > -1 ))
    {
      // Remove any remaining mask characters from the truly numeric one; can't do anything about the phone parens and dash on partially completed phones
      newValue = newValue.replace(/_/g, '');

      if (name.toLowerCase().indexOf("zip") > -1 && newValue.length <= 6)
      {
        // Chop the trailling dash
        newValue = newValue.substring(0, newValue.length - 1); // Comes through as 980- for example
      }
    }

    localData[name] = newValue
    setLocalData(localData)
  }

  const handleSpecialtiesChanged = (selectedSpecialties)  => {
    var specialties = [];
    for(var i=0; i<selectedSpecialties.length; i++)
    {
      var index = selectedSpecialties[i].label.indexOf(":");
      var code = selectedSpecialties[i].label.substring(0, index);
      var name = selectedSpecialties[i].label.substring(index+2);

      specialties.push({id: selectedSpecialties[i].value, code: code, name: name});
    }

    localData["specialties"] = specialties
    setLocalData(localData)

    // TODO - doesn't trigger the refresh with the specialty subobject update, so using this hack for now
    setForceSpecialtyRefresh(forceSpecialtyRefresh + 1);
  }

  var selectedSpecialtiesTransformed = [];
  if(localData.specialties)
  {
    for(var i = 0; i < localData.specialties.length; i++)
    {
      selectedSpecialtiesTransformed.push({value: localData.specialties[i].id, label: localData.specialties[i].code + ": " + localData.specialties[i].name});
    }
  }


  if (!isLoaded) {
    return <div>Loading...</div>
  }
  else if (error || !data)
  {
    return <div>Something went wrong... please reload the page...</div>
  }
  else
  {
    let showLeftCol = (isH3Admin || authorities.includes("PRACTICE_SENSITIVE_DETAILS_VIEW") || authorities.includes("PRACTICE_SENSITIVE_DETAILS_EDIT")
      || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT"));

    // Quick-nav targets (ids match the wrappers below); only sections this user can see
    const canSee = (permission) => isH3Admin || authorities.includes(permission + "_VIEW") || authorities.includes(permission + "_EDIT");
    const sectionNav = [
      { id: "provider-enrollment-status", label: "Enrollment Status", show: !data.excludedFromCarrierCredentialing },
      { id: "location-status", label: "Location Status", show: canSee("PROVIDER_FACILITY") },
      { id: "data-hub-access", label: "Data Hub Access", show: canSee("PRACTICE_DATA_HUB") },
      { id: "documents", label: "Documents", show: canSee("PRACTICE_DOCUMENT") },
    ].filter((section) => section.show);

    return (
        <React.Fragment>
            <Helmet title={data.name} />
            <Container fluid className="p-0" style={{marginTop: "-20px"}}>
            <PracticeHeader
              data={data}
              onboardingStatus={<PracticeOnboardingUrl practiceId={id} />}
              canEdit={isH3Admin || authorities.includes("PRACTICE_BASIC_EDIT")}
              isH3Admin={isH3Admin}
              onEditClicked={handleEditClicked}
              sections={sectionNav}
            />
            <PracticeEditPanel
              show={editing}
              data={data}
              localData={localData}
              onValueChanged={onValueChanged}
              onSave={() => {handleSaveClicked(false, false)}}
              onCancel={handleCancelClicked}
              forceSpecialtyRefresh={forceSpecialtyRefresh}
              selectedSpecialties={selectedSpecialtiesTransformed}
              onSpecialtiesChanged={handleSpecialtiesChanged}
            />
            <Row>
                {showLeftCol ?
                <Col xl="3">
                  {(isH3Admin || authorities.includes("PRACTICE_SENSITIVE_DETAILS_VIEW") || authorities.includes("PRACTICE_SENSITIVE_DETAILS_EDIT")) ?
                  <>
                  <Card style={cardOutlineStyle}>
                  <DetailCardHeader
                    title="Practice Details"
                    canEdit={isH3Admin || authorities.includes("PRACTICE_SENSITIVE_DETAILS_EDIT")}
                    editing={editingDetails}
                    expanded={isExpanded}
                    onToggle={(e) => (isExpanded ? handleCollapseClicked(e) : handleExpandClicked(e))}
                    onEdit={() => {handleEditDetailsClicked()}}
                    onSave={() => {handleSaveClicked(true, false)}}
                    onCancel={() => {handleCancelClicked()}}
                  />
                  <Collapse in={isExpanded}>
                    <div className="px-4 pb-3">
                      <DetailSection title="Identity" first inline={!editingDetails}>
                        <DetailField label="NPI">
                          {editingDetails?
                          <EditableDetail originalValue={localData.npi} name="npi" mask="9999999999" onValueChanged={onValueChanged}/>
                          :
                          <span style={{fontFamily: "monospace"}}><CopyText text={localData.npi} /></span>
                          }
                        </DetailField>
                        <DetailField label="Grouping">
                          {editingDetails?
                          <EditableDetail originalValue={localData.grouping} name="grouping" onValueChanged={onValueChanged}/>
                          :
                          localData.grouping
                          }
                        </DetailField>
                        <DetailField label="Telehealth">
                          {editingDetails?
                          <EditableDetail originalValue={localData.telehealth} name="telehealth" inputType="checkbox" onValueChanged={onValueChanged}/>
                          :
                          localData.telehealth ? "Yes" : "No"
                          }
                        </DetailField>
                      </DetailSection>

                      <DetailSection title="Locations">
                        <DetailField label="Address">
                          {editingDetails ?
                          <EditableAddress address1={localData.address1} address2={localData.address2} city={localData.city} state={localData.state} zip={localData.zip} county={localData.county} onValueChanged={onValueChanged} />
                          :
                          <ReadOnlyAddress copyable address1={localData.address1} address2={localData.address2} city={localData.city} state={localData.state} zip={localData.zip} county={localData.county} />
                          }
                        </DetailField>
                        <DetailField label="Mailing Address">
                          {editingDetails ?
                          <EditablMailingAddress address1={localData.mailingAddress1} address2={localData.mailingAddress2} city={localData.mailingCity} state={localData.mailingState} zip={localData.mailingZip} county={localData.mailingCounty} onValueChanged={onValueChanged} />
                          :
                          <ReadOnlyAddress copyable address1={localData.mailingAddress1} address2={localData.mailingAddress2} city={localData.mailingCity} state={localData.mailingState} zip={localData.mailingZip} county={localData.mailingCounty} />
                          }
                        </DetailField>
                        <DetailField label="Remit Address">
                          {editingDetails ?
                          <EditablRemitAddress address1={localData.remitAddress1} address2={localData.remitAddress2} city={localData.remitCity} state={localData.remitState} zip={localData.remitZip} county={localData.remitCounty} onValueChanged={onValueChanged} />
                          :
                          <ReadOnlyAddress copyable address1={localData.remitAddress1} address2={localData.remitAddress2} city={localData.remitCity} state={localData.remitState} zip={localData.remitZip} county={localData.remitCounty} />
                          }
                        </DetailField>
                        <DetailField label="Office Hours">
                          {editingDetails?
                          <EditableDetail originalValue={localData.officeHours} name="officeHours" onValueChanged={onValueChanged}/>
                          :
                          <CopyText text={localData.officeHours} />
                          }
                        </DetailField>
                      </DetailSection>

                      <DetailSection title="Group IDs" inline={!editingDetails}>
                        <DetailField label="Group Medicare">
                          {editingDetails?
                          <EditableDetail originalValue={localData.groupMedicare} name="groupMedicare" onValueChanged={onValueChanged}/>
                          :
                          <CopyIds value={localData.groupMedicare} />
                          }
                        </DetailField>
                        <DetailField label="Group Medicaid">
                          {editingDetails?
                          <EditableDetail originalValue={localData.groupMedicaid} name="groupMedicaid" onValueChanged={onValueChanged}/>
                          :
                          <CopyIds value={localData.groupMedicaid} />
                          }
                        </DetailField>
                        <DetailField label="Other Group IDs">
                          {editingDetails?
                          <EditableDetail originalValue={localData.otherGroupIds} name="otherGroupIds" onValueChanged={onValueChanged}/>
                          :
                          <CopyIds value={localData.otherGroupIds} />
                          }
                        </DetailField>
                      </DetailSection>
                    </div>
                  </Collapse>
                  </Card>

                  <Card style={cardOutlineStyle}>
                  <DetailCardHeader
                    title="Practice Settings"
                    canEdit={isH3Admin || authorities.includes("PRACTICE_SENSITIVE_DETAILS_EDIT")}
                    editing={editingSettings}
                    expanded={isSettingsExpanded}
                    onToggle={(e) => (isSettingsExpanded ? handleSettingsCollapseClicked(e) : handleSettingsExpandClicked(e))}
                    onEdit={() => {handleEditSettingsClicked()}}
                    onSave={() => {handleSaveClicked(false, true)}}
                    onCancel={() => {handleCancelClicked()}}
                  />
                  <Collapse in={isSettingsExpanded}>
                    <div className="px-4 pb-3">
                      <DetailSection title="Notifications" first>
                        <DetailField label="Enrollment notification emails" hint={editingSettings ? "Comma-separated. Overrides review team." : null}>
                          {editingSettings?
                          <EditableDetail originalValue={localData.enrollmentNotificationEmails} name="enrollmentNotificationEmails" onValueChanged={onValueChanged}/>
                          :
                          <EmailList value={localData.enrollmentNotificationEmails} />
                          }
                        </DetailField>
                        <DetailField label="Onboarding notification CC emails" hint={editingSettings ? "Comma-separated." : null}>
                          {editingSettings?
                          <EditableDetail originalValue={localData.onboardingNotificationCcEmails} name="onboardingNotificationCcEmails" onValueChanged={onValueChanged}/>
                          :
                          <EmailList value={localData.onboardingNotificationCcEmails} />
                          }
                        </DetailField>
                      </DetailSection>
                    </div>
                  </Collapse>
                  </Card>
                  </>
                  : <></>
                  }
                  {(isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT")) ?
                    <OpenTasks entityType="practice" entityId={id} />
                  : <></>
                  }
                </Col>
                : <></>
                }
                <Col xl={showLeftCol ? "9" : "12"}>
                  {!data.excludedFromCarrierCredentialing &&
                  <div id="provider-enrollment-status" className="provider-section"><ProviderSortableList key={"provider-" + id} practiceId={id} /></div>
                  }

                  {(isH3Admin || authorities.includes("PROVIDER_FACILITY_VIEW") || authorities.includes("PROVIDER_FACILITY_EDIT")) ?
                  <div id="location-status" className="provider-section"><ProviderSortableList key={"facility-" + id} practiceId={id} isFacilityList={true} /></div>
                  : <></>
                  }
  
                  {(isH3Admin || authorities.includes("PRACTICE_DATA_HUB_VIEW") || authorities.includes("PRACTICE_DATA_HUB_EDIT")) ?
                  <div id="data-hub-access" className="provider-section"><HubParticipationStatusTable entityType="practice" entityId={id} entityName={data.name} canEdit={isH3Admin || authorities.includes("PRACTICE_DATA_HUB_EDIT")} /></div>
                  : <></>
                  }

                  {(isH3Admin || authorities.includes("PRACTICE_DOCUMENT_VIEW") || authorities.includes("PRACTICE_DOCUMENT_EDIT")) ?
                  <div id="documents" className="provider-section"><Card style={cardOutlineStyle}>
                    <Card.Header style={{marginBottom: '-25px'}}>
                      <Card.Title style={{marginBottom: '-15px'}}>Documents</Card.Title>
                      <br />
                    </Card.Header>
                    <Card.Body style={{overflowY: 'scroll', maxHeight: '700px'}}>
                      <DocumentList entityId={id} entityType="practice" canEdit={isH3Admin || authorities.includes("PRACTICE_DOCUMENT_EDIT")} />
                    </Card.Body>
                  </Card></div>
                  : <></>
                  }
                </Col>
            </Row>
            </Container>
        </React.Fragment>
    );
  }
};

export default Practice;
